import { useTeamsAuth } from '@teams/hooks/useTeamsAuth';
import styles from '@teams/Root.module.scss';
import { teamsPaths } from '@teams/routes/AppRoutes';
import { ScenarioInsightsLegacyDrawerRender } from '@terminal/components/insights-legacy-drawer/ScenarioInsightsLegacyDrawerRender';
import { Loader } from '@terminal/components/loader/Loader';
import { ToastsLayout } from '@terminal/components/toasts-layout/ToastsLayout';
import { config } from '@terminal/config';
import { useNotificationsListener } from '@terminal/hooks/use-notifications-listener/useNotificationsListener';
import { useAuthStore } from '@terminal/stores/use-auth/store/authStore';
import { getSession, useAuth } from '@terminal/stores/use-auth/useAuth';
import { useLivePriceWebSocketStore } from '@terminal/stores/use-live-price-websocket-store/useLivePriceWebSocketStore';
import { useNotifications } from '@terminal/stores/use-notifications/useNotifications';
import { FC, Suspense, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Navigate, Outlet, useLocation } from 'react-router';

// Detect whether we're actually running inside the Teams client.
// Outside Teams (direct browser / dev mode), window.parent === window.
const isInsideTeams = () =>
  typeof window !== 'undefined' && window.parent !== window;

export const Root: FC = () => {
  const { authProcessing, initStores, setAuthProcessing } = useAuth();
  const isAuthenticated = useAuthStore(state => !!state.token);
  const { initiateLogin } = useTeamsAuth();
  const location = useLocation();
  useNotificationsListener();

  const isAuthPopup = location.pathname === teamsPaths.authPopup;
  useEffect(() => {
    const initSession = async () => {
      if (isAuthPopup) {
        setAuthProcessing(false);
        return;
      }

      try {
        // Cookie-based session works in Teams desktop client and direct browser access
        const session = await getSession();

        if (session) {
          await initStores(session);
          useNotifications.getState().initialize();
          setAuthProcessing(false);
          return;
        }

        if (isInsideTeams()) {
          // Inside Teams but no cookie session — open the Teams auth popup.
          // authProcessing is cleared by the login callbacks, not here.
          initiateLogin({
            onSuccess: async authData => {
              try {
                await initStores(authData);
                useNotifications.getState().initialize();
              } finally {
                setAuthProcessing(false);
              }
            },
            onFailure: () => {
              setAuthProcessing(false);
            },
          });
        } else {
          // Outside Teams (dev browser) and no session — show auth popup login page directly
          setAuthProcessing(false);
        }
      } catch {
        // getSession() or initStores() threw — ensure the loading state is always cleared
        setAuthProcessing(false);
      }
    };

    initSession();

    return () => {
      useLivePriceWebSocketStore.getState().closeConnection();
    };
  }, []);

  if (authProcessing && !isAuthPopup) {
    return <Loader />;
  }

  // Outside Teams with no session: redirect to the login page
  if (!isAuthenticated && !isAuthPopup) {
    return <Navigate to={teamsPaths.authPopup} replace />;
  }

  return (
    <>
      <Helmet>
        <meta name="tog-version" content={config.app.version + '-teams'} />
      </Helmet>
      <div className={styles.appWrapper}>
        <Suspense fallback={<Loader />}>
          <Outlet />
        </Suspense>
        <ScenarioInsightsLegacyDrawerRender />
      </div>
      <ToastsLayout />
    </>
  );
};
